import { useEffect, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { Users, ShoppingBag, Package, IndianRupee, PlusCircle, Trash2, Download, Send, Upload, CheckCircle2, Pencil, Bell } from "lucide-react";
import { toast } from "sonner";
import AdminPayouts from "@/components/AdminPayouts";
import AdminReturnsTab from "@/components/returns/AdminReturnsTab";
import CommissionFeesTab from "@/components/CommissionFeesTab";
import AdminBoostsTab from "@/components/AdminBoostsTab";
import AdminDropsTab from "@/components/AdminDropsTab";
import AdminLiveTab from "@/components/AdminLiveTab";
import AdminCustomerAnalytics from "@/components/AdminCustomerAnalytics";
import AdminGrievanceTab from "@/components/AdminGrievanceTab";
import AdminTeamTab from "@/components/AdminTeamTab";
import AdminExportsTab from "@/components/AdminExportsTab";
import AdminBankTab from "@/components/AdminBankTab";
import AdminCodSettings from "@/components/AdminCodSettings";
import AdminRefundSettings from "@/components/AdminRefundSettings";
import AdminRefundControl from "@/components/AdminRefundControl";
import AdminOrderActions from "@/components/AdminOrderActions";
import AdminCuratedDropCard from "@/components/AdminCuratedDropCard";
import AdminMenuLinksCard from "@/components/AdminMenuLinksCard";
import VerifiedBadge from "@/components/VerifiedBadge";
import { useSiteContent } from "@/context/SiteContentContext";
import { maskAadhaar } from "@/lib/mask";

const COLORS = ["#F48E2E", "#FF9800", "#2196F3", "#4CAF50", "#9C27B0", "#F44336"];
const CATS = [
  { slug: "chaniya-choli", name: "Chaniya Choli" },
  { slug: "gift-articles", name: "Gift Articles" },
  { slug: "accessories", name: "Accessories" },
  { slug: "womens-fashion", name: "Women's Fashion" },
  { slug: "mens-fashion", name: "Men's Fashion" },
  { slug: "kids", name: "Kids" },
  { slug: "trending", name: "Trending" },
];

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const { refresh: refreshSiteContent } = useSiteContent();
  const [analytics, setAnalytics] = useState(null);
  const [products, setProducts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderFilter, setOrderFilter] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [payActionBusy, setPayActionBusy] = useState("");
  const [customers, setCustomers] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [notif, setNotif] = useState({ title: "", body: "", target: "all" });
  const [announceForm, setAnnounceForm] = useState({ title: "", body: "", link: "/" });
  const [announcements, setAnnouncements] = useState([]);
  const [announceBusy, setAnnounceBusy] = useState(false);
  const [openP, setOpenP] = useState(false);
  const [openB, setOpenB] = useState(false);
  const [prod, setProd] = useState({ name: "", description: "", price: "", original_price: "", category: "trending", image_base64: "", stock: 10, duration_hours: 48 });
  const [banner, setBanner] = useState({ title: "", subtitle: "", image_base64: "", cta_text: "Shop Now", cta_link: "/", active: true, order: 0, show_overlay: false });
  const [legalDocs, setLegalDocs] = useState([]);
  const [legalEditing, setLegalEditing] = useState(null); // slug
  const [legalDraft, setLegalDraft] = useState("");
  const [legalBusy, setLegalBusy] = useState(false);
  const [siteContent, setSiteContent] = useState({});
  const [contentBusy, setContentBusy] = useState({});
  const [cats, setCats] = useState([]);
  const [catForm, setCatForm] = useState({ slug: "", name: "", icon: "", display_order: 0, parent_slug: "", gst_default: 18 });
  const [catEditing, setCatEditing] = useState(null); // slug being edited
  const [catBusy, setCatBusy] = useState(false);
  const [tab, setTab] = useState("analytics");

  useEffect(() => {
    if (authLoading) return;
    if (!user) return nav("/login");
    if (user.role !== "admin") return nav("/");
    reload();
  }, [user, authLoading]);

  // ---- Admin notifications: background poll + sound alert on new arrivals (e.g. new KYC submitted) ----
  const [adminNotifs, setAdminNotifs] = useState([]);
  const [notifPanelOpen, setNotifPanelOpen] = useState(false);
  const [lastViewedNotifAt, setLastViewedNotifAt] = useState(
    () => localStorage.getItem("admin_notif_last_viewed") || new Date(0).toISOString()
  );
  const knownNotifIdsRef = useRef(null);
  const unreadNotifCount = adminNotifs.filter((n) => new Date(n.created_at) > new Date(lastViewedNotifAt)).length;

  const playNotificationSound = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(); osc.stop(ctx.currentTime + 0.5);
    } catch { /* audio not available in this browser/context */ }
  };

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    const poll = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (knownNotifIdsRef.current) {
          const isNew = data.some((n) => !knownNotifIdsRef.current.has(n.id));
          if (isNew) playNotificationSound();
        }
        knownNotifIdsRef.current = new Set(data.map((n) => n.id));
        setAdminNotifs(data);
      } catch { /* skip a beat, try again next interval */ }
    };
    poll();
    const id = setInterval(poll, 20000);
    return () => clearInterval(id);
  }, [user]);

  const openNotifPanel = () => {
    setNotifPanelOpen((v) => !v);
    const now = new Date().toISOString();
    setLastViewedNotifAt(now);
    localStorage.setItem("admin_notif_last_viewed", now);
  };

  // Bank-transfer orders wait for the admin to confirm the money arrived.
  const isAwaitingBankTransfer = (o) => o.payment_method === "bank_transfer" && o.payment_status === "awaiting_confirmation" && o.status !== "cancelled";
  const bankTransferAction = async (o, action) => {
    const msg = action === "mark-payment-received"
      ? `Mark the bank transfer for order ${o.id.slice(0, 8).toUpperCase()} as received? The order will move to the vendor for confirmation.`
      : `Reject the bank transfer for order ${o.id.slice(0, 8).toUpperCase()}? The order will be cancelled and stock returned.`;
    if (!window.confirm(msg)) return;
    setPayActionBusy(o.id);
    try {
      await api.post(`/admin/orders/${o.id}/${action}`, {});
      toast.success(action === "mark-payment-received" ? "Payment marked as received. The vendor has been notified." : "Payment rejected. Order cancelled and stock returned.");
      const { data } = await api.get("/admin/orders?include_archived=true");
      setOrders(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setPayActionBusy("");
    }
  };

  const reloadSeqRef = useRef(0);
  const reload = async () => {
    const seq = ++reloadSeqRef.current;
    const [a, p, b, v, o, c, py, lg, sc, cs, ann] = await Promise.allSettled([
      api.get("/admin/analytics"), api.get("/products?include_expired=true"),
      api.get("/banners"), api.get("/admin/vendors"),
      api.get("/admin/orders?include_archived=true"), api.get("/admin/customers"),
      api.get("/admin/vendor-payouts"),
      api.get("/admin/legal"),
      api.get("/site-content"),
      api.get("/categories"),
      api.get("/admin/announcements"),
    ]);
    // React StrictMode (dev) double-invokes the mount effect, so overlapping
    // reload() calls can resolve out of order. If a newer reload() has since
    // been kicked off, this one is stale -- drop its results rather than let
    // it clobber fresher state (e.g. a just-deleted category reappearing).
    if (reloadSeqRef.current !== seq) return;
    // Each field updates independently — one failed/aborted request (e.g. a
    // cancelled in-flight call) must not block the rest of the dashboard
    // from refreshing after an action like a category delete.
    if (a.status === "fulfilled") setAnalytics(a.value.data);
    if (p.status === "fulfilled") setProducts(p.value.data);
    if (b.status === "fulfilled") setBanners(b.value.data);
    if (v.status === "fulfilled") setVendors(v.value.data);
    if (o.status === "fulfilled") setOrders(o.value.data);
    if (c.status === "fulfilled") setCustomers(c.value.data);
    if (py.status === "fulfilled") setPayouts(py.value.data);
    if (lg.status === "fulfilled") setLegalDocs(lg.value.data);
    if (sc.status === "fulfilled") setSiteContent(sc.value.data || {});
    if (cs.status === "fulfilled") setCats(cs.value.data || []);
    if (ann.status === "fulfilled") setAnnouncements(ann.value.data || []);
  };

  const resetCatForm = () => { setCatForm({ slug: "", name: "", icon: "", display_order: cats.length, parent_slug: "", gst_default: 18 }); setCatEditing(null); };
  const startCatEdit = (c) => { setCatEditing(c.slug); setCatForm({ slug: c.slug, name: c.name, icon: c.icon || "", display_order: c.display_order ?? 0, parent_slug: c.parent_slug || "", gst_default: c.gst_default ?? 18 }); };
  const saveCategory = async () => {
    if (!catForm.name.trim()) return toast.error("Category name is required");
    setCatBusy(true);
    try {
      if (catEditing) {
        await api.patch(`/admin/categories/${catEditing}`, {
          name: catForm.name.trim(), icon: catForm.icon || "",
          display_order: Number(catForm.display_order || 0),
          parent_slug: catForm.parent_slug || null,
          gst_default: Number(catForm.gst_default),
        });
        toast.success("Category updated");
      } else {
        const slug = (catForm.slug || catForm.name).trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
        if (!slug) return toast.error("Enter a slug");
        await api.post("/admin/categories", {
          slug, name: catForm.name.trim(), icon: catForm.icon || "",
          display_order: Number(catForm.display_order || 0),
          parent_slug: catForm.parent_slug || null,
          gst_default: Number(catForm.gst_default),
        });
        toast.success("Category created");
      }
      resetCatForm(); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setCatBusy(false); }
  };
  const deleteCategory = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/categories/${slug}`); toast.success("Category removed"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveContent = async (key, value) => {
    setContentBusy((b) => ({ ...b, [key]: true }));
    try {
      await api.patch(`/admin/site-content/${key}`, { value });
      toast.success("Saved");
      setSiteContent((s) => ({ ...s, [key]: value }));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setContentBusy((b) => ({ ...b, [key]: false })); }
  };

  const startLegalEdit = (doc) => {
    setLegalEditing(doc.slug);
    setLegalDraft(doc.body_html || "");
  };

  const saveLegal = async () => {
    setLegalBusy(true);
    try {
      await api.patch(`/admin/legal/${legalEditing}`, { body_html: legalDraft });
      toast.success("Policy updated");
      setLegalEditing(null); setLegalDraft("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally { setLegalBusy(false); }
  };

  const readFile = (setter, key) => (e) => {
    const f = e.target.files[0]; if (!f) return;
    const r = new FileReader();
    r.onload = (ev) => setter((prev) => ({ ...prev, [key]: ev.target.result }));
    r.readAsDataURL(f);
  };

  const toggleProdVisible = async (p) => {
    try {
      await api.patch(`/products/${p.id}`, { visible: p.visible === false ? true : false });
      toast.success(p.visible === false ? "Product activated" : "Product deactivated");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const saveProdHours = async (id) => {
    const h = Number(document.getElementById(`hours-${id}`)?.value);
    if (!h || h < 48 || h > 240) return toast.error("Duration must be between 48 and 240 hours");
    try {
      await api.patch(`/products/${id}`, { duration_hours: h });
      toast.success("Display duration updated");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const createProd = async () => {
    if (!prod.image_base64) return toast.error("Upload image");
    try {
      await api.post("/products", {
        ...prod,
        price: Number(prod.price), original_price: Number(prod.original_price || prod.price),
        stock: Number(prod.stock), duration_hours: Number(prod.duration_hours),
      });
      toast.success("Added"); setOpenP(false); reload();
      setProd({ name: "", description: "", price: "", original_price: "", category: "trending", image_base64: "", stock: 10, duration_hours: 48 });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const delProd = async (id) => { await api.delete(`/products/${id}`); reload(); };

  const createBanner = async () => {
    if (!banner.image_base64) return toast.error("Upload banner image");
    await api.post("/banners", banner); toast.success("Banner added");
    setOpenB(false); reload();
    setBanner({ title: "", subtitle: "", image_base64: "", cta_text: "Shop Now", cta_link: "/", active: true, order: 0, show_overlay: false });
  };
  const delBanner = async (id) => { await api.delete(`/banners/${id}`); reload(); };
  const toggleBannerOverlay = async (b) => {
    try {
      await api.patch(`/banners/${b.id}`, { show_overlay: !b.show_overlay });
      setBanners((rows) => rows.map((r) => r.id === b.id ? { ...r, show_overlay: !b.show_overlay } : r));
      toast.success(!b.show_overlay ? "Text overlay is now visible on this banner." : "Text overlay hidden — clean image only.");
    } catch { toast.error("Could not update banner"); }
  };

  const verifyVendor = async (id) => {
    try {
      await api.post(`/admin/vendors/${id}/verify`);
      toast.success("Vendor approved");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not approve this vendor");
    }
  };
  const [kycRejectDialog, setKycRejectDialog] = useState({ open: false, vendor: null, reason: "" });
  const openKycRejectDialog = (vendor) => setKycRejectDialog({ open: true, vendor, reason: "" });
  const submitKycReject = async () => {
    const { vendor, reason } = kycRejectDialog;
    if (!vendor) return;
    try {
      await api.post(`/admin/vendors/${vendor.id}/reject`, { reason: reason.trim() });
      toast.success("Vendor KYC rejected");
      setKycRejectDialog({ open: false, vendor: null, reason: "" });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reject"); }
  };
  const setVerifiedOverride = async (id, override) => {
    try {
      await api.post(`/admin/vendors/${id}/verified-override`, { override });
      toast.success(
        override === true ? "Vendor manually marked as Verified." :
        override === false ? "Vendor manually marked as Unverified." :
        "Manual override cleared — auto rules apply."
      );
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };

  const [statusDialog, setStatusDialog] = useState({ open: false, vendor: null, target: "paused", reason: "" });
  const openStatusDialog = (vendor, target) => setStatusDialog({ open: true, vendor, target, reason: "" });
  const submitStatusChange = async () => {
    const { vendor, target, reason } = statusDialog;
    if (!vendor) return;
    if ((target === "paused" || target === "suspended") && !reason.trim()) {
      return toast.error("Please write a short reason so the vendor knows why.");
    }
    try {
      await api.post(`/admin/vendors/${vendor.id}/status`, { status: target, reason: reason.trim() });
      toast.success(
        target === "active" ? "Vendor set to Active." :
        target === "paused" ? "Vendor paused — uploads blocked, listings stay live." :
        "Vendor suspended — listings hidden, sign-in blocked."
      );
      setStatusDialog({ open: false, vendor: null, target: "paused", reason: "" });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update status");
    }
  };
  const setActive = async (vendor) => {
    try {
      await api.post(`/admin/vendors/${vendor.id}/status`, { status: "active" });
      toast.success("Vendor set to Active.");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update status"); }
  };
  const settlePayout = async (vendor_id, business_name, pending) => {
    if (!pending || pending <= 0) return toast.error(`No pending payout for ${business_name}`);
    if (!window.confirm(`Mark ₹${pending.toLocaleString("en-IN")} as paid out to ${business_name}? This will move all delivered orders of this exhibitor to the 'Paid Out' bucket.`)) return;
    const { data } = await api.post(`/admin/vendors/${vendor_id}/settle-payout`);
    toast.success(`Settled ₹${data.amount.toLocaleString("en-IN")} across ${data.orders_settled} order(s)`);
    reload();
  };

  const downloadCsv = (filename, headers, rows) => {
    const escape = (val) => `"${(val ?? "").toString().replace(/"/g, '""')}"`;
    const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const exportVendorsCsv = () => {
    if (!vendors.length) return toast.error("No vendors to export");
    const headers = ["vendor_code","email","full_name","phone","kyc_status","verified","business_name","owner_name","pan_number","aadhaar_number","gst_number","msme_number","business_address","bank_holder_name","bank_account_number","bank_ifsc","instagram_handle","facebook_handle","website_url","submitted_at","created_at"];
    const rows = vendors.map((v) => ({
      vendor_code: v.vendor_code,
      email: v.email, full_name: v.full_name, phone: v.phone,
      kyc_status: v.kyc_status, verified: v.verified,
      business_name: v.kyc?.business_name, owner_name: v.kyc?.owner_name,
      pan_number: v.kyc?.pan_number, aadhaar_number: maskAadhaar(v.kyc?.aadhaar_number),
      gst_number: v.kyc?.gst_number, msme_number: v.kyc?.msme_number,
      business_address: v.kyc?.business_address,
      bank_holder_name: v.kyc?.bank_holder_name,
      bank_account_number: v.kyc?.bank_account_number,
      bank_ifsc: v.kyc?.bank_ifsc,
      instagram_handle: v.kyc?.instagram_handle,
      facebook_handle: v.kyc?.facebook_handle,
      website_url: v.kyc?.website_url,
      submitted_at: v.kyc?.submitted_at,
      created_at: v.created_at,
    }));
    downloadCsv("cityshor_vendor_registrations.csv", headers, rows);
  };

  const exportPayoutsCsv = () => {
    if (!payouts.length) return toast.error("No payout data");
    const headers = ["vendor_code","business_name","owner_name","email","phone","kyc_status","units_sold","gross","commission","net_earnings","pending_payout","paid_out","bank_holder","bank_account","bank_ifsc","pan_number","gst_number"];
    downloadCsv("cityshor_vendor_payouts.csv", headers, payouts);
  };

  const exportGstReport = async () => {
    try {
      const { data } = await api.get("/admin/gst-report");
      if (!data.length) return toast.error("No orders to report");
      const headers = ["invoice_no","invoice_date","buyer_name","buyer_state","buyer_pincode","place_of_supply","supplier_gstin","supplier_name","vendor_codes","gross_amount","taxable_value","gst_type","cgst_9","sgst_9","igst_18","total_gst","cod_charges","invoice_total","payment_method","payment_status","order_status"];
      downloadCsv("cityshor_gst_report_gstr1.csv", headers, data);
      toast.success(`Exported ${data.length} invoice rows`);
    } catch { toast.error("Failed to export GST report"); }
  };

  const exportVendorGstReport = async () => {
    try {
      const { data } = await api.get("/admin/vendor-gst-report");
      if (!data.length) return toast.error("No vendor sales yet");
      const headers = ["vendor_code","business_name","email","orders","units","gross_amount","taxable_value","gst_portion","commission","net_payout"];
      downloadCsv("cityshor_gst_vendor_summary.csv", headers, data);
      toast.success(`Exported ${data.length} exhibitors`);
    } catch { toast.error("Failed to export vendor GST report"); }
  };

  const sendNotif = async () => {
    if (!notif.title) return toast.error("Title required");
    await api.post("/admin/notifications", notif);
    toast.success("Notification sent");
    setNotif({ title: "", body: "", target: "all" });
  };

  const sendAnnouncement = async () => {
    if (!announceForm.title.trim() || !announceForm.body.trim()) return toast.error("Title and body are required");
    setAnnounceBusy(true);
    try {
      const { data } = await api.post("/admin/announcements", { ...announceForm, link: announceForm.link || "/" });
      const fcm = data.fcm_result || {};
      toast.success(`Announcement sent to ${data.buyer_count} buyer${data.buyer_count === 1 ? "" : "s"}${fcm.skipped ? " (in-app only — FCM not configured yet)" : fcm.sent ? ` · ${fcm.sent} push delivered` : ""}`);
      setAnnounceForm({ title: "", body: "", link: "/" });
      const { data: list } = await api.get("/admin/announcements");
      setAnnouncements(list || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send announcement");
    } finally { setAnnounceBusy(false); }
  };

  const resendAnnouncement = async (id) => {
    if (!window.confirm("Send this announcement to all buyers again?")) return;
    try {
      const { data } = await api.post(`/admin/announcements/${id}/resend`);
      toast.success(`Re-sent to ${data.buyer_count} buyer${data.buyer_count === 1 ? "" : "s"}`);
      const { data: list } = await api.get("/admin/announcements");
      setAnnouncements(list || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not resend"); }
  };

  const exportCsv = () => {
    if (!customers.length) return toast.error("No data");
    const headers = ["email","full_name","phone","city","state","pincode","gender","age","total_orders","total_spent_paise","last_order_at"];
    const rows = customers.map((c) => headers.map((h) => `"${(c[h] ?? "").toString().replace(/"/g, '""')}"`).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "cityshor_customers.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  if (!user) return <div className="p-10">Loading…</div>;
  const T = analytics?.totals || { buyers: 0, vendors: 0, products: 0, orders: 0, revenue: 0 };

  const stat = (icon, label, val, testid, goToTab) => (
    <div
      className={`p-5 rounded-2xl bg-white border border-gray-200 ${goToTab ? "cursor-pointer hover:border-[#F48E2E] transition-colors" : ""}`}
      data-testid={testid}
      onClick={goToTab ? () => setTab(goToTab) : undefined}
      role={goToTab ? "button" : undefined}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-gray-500">{label}</div>
        <div className="p-2 rounded-full bg-[#FEF3E6] text-[#F48E2E]">{icon}</div>
      </div>
      <div className="font-display text-3xl">{val}</div>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 lg:px-8 py-10" data-testid="admin-dashboard">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#F48E2E] font-bold mb-2">Admin</div>
          <h1 className="font-display text-4xl">Control Center</h1>
        </div>
        <div className="relative">
          <button
            type="button"
            data-testid="admin-notif-bell"
            onClick={openNotifPanel}
            className="relative p-2.5 rounded-full border border-gray-200 bg-white hover:bg-gray-50"
            aria-label="Notifications"
          >
            <Bell className="h-5 w-5 text-gray-700" />
            {unreadNotifCount > 0 && (
              <span data-testid="admin-notif-badge" className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                {unreadNotifCount > 9 ? "9+" : unreadNotifCount}
              </span>
            )}
          </button>
          {notifPanelOpen && (
            <div data-testid="admin-notif-panel" className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white border border-gray-200 rounded-2xl shadow-lg z-50 p-2">
              {adminNotifs.length === 0 ? (
                <div className="text-sm text-gray-500 py-8 text-center">No notifications yet.</div>
              ) : adminNotifs.map((n) => (
                <div key={n.id} className="p-3 hover:bg-gray-50 rounded-xl">
                  <div className="text-sm font-medium">{n.title}</div>
                  <div className="text-xs text-gray-600 mt-0.5">{n.body}</div>
                  <div className="text-[11px] text-gray-400 mt-1">{new Date(n.created_at).toLocaleString("en-IN")}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        {stat(<Users className="h-4 w-4" />, "Buyers", T.buyers, "stat-buyers", "customers")}
        {stat(<Package className="h-4 w-4" />, "Vendors", T.vendors, "stat-vendors", "vendors")}
        {stat(<ShoppingBag className="h-4 w-4" />, "Products", T.products, "stat-products", "products")}
        {stat(<ShoppingBag className="h-4 w-4" />, "Orders", T.orders, "stat-orders", "orders")}
        {stat(<IndianRupee className="h-4 w-4" />, "Revenue", `₹${(T.revenue || 0).toLocaleString("en-IN")}`, "stat-revenue", "analytics")}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-gray-100">
          <TabsTrigger data-testid="tab-analytics" value="analytics">Analytics</TabsTrigger>
          <TabsTrigger data-testid="tab-products" value="products">Products</TabsTrigger>
          <TabsTrigger data-testid="tab-banners" value="banners">Banners</TabsTrigger>
          <TabsTrigger data-testid="tab-vendors" value="vendors">Vendors</TabsTrigger>
          <TabsTrigger data-testid="tab-payouts" value="payouts">Payouts</TabsTrigger>
          <TabsTrigger data-testid="tab-returns" value="returns">Returns</TabsTrigger>
          <TabsTrigger data-testid="tab-orders" value="orders">Orders</TabsTrigger>
          <TabsTrigger data-testid="tab-customers" value="customers">Customers</TabsTrigger>
          <TabsTrigger data-testid="tab-notifications" value="notifications">Notify</TabsTrigger>
          <TabsTrigger data-testid="tab-legal" value="legal">Legal</TabsTrigger>
          <TabsTrigger data-testid="tab-content" value="content">Content</TabsTrigger>
          <TabsTrigger data-testid="tab-fees" value="fees">Fees</TabsTrigger>
          <TabsTrigger data-testid="tab-boosts" value="boosts">Boosts</TabsTrigger>
          <TabsTrigger data-testid="tab-drops" value="drops">Drops</TabsTrigger>
          <TabsTrigger data-testid="tab-live" value="live">Live</TabsTrigger>
          <TabsTrigger data-testid="tab-categories" value="categories">Categories</TabsTrigger>
          <TabsTrigger data-testid="tab-grievance" value="grievance">Grievance</TabsTrigger>
          <TabsTrigger data-testid="tab-team" value="team">Team</TabsTrigger>
          <TabsTrigger data-testid="tab-exports" value="exports">Exports</TabsTrigger>
          <TabsTrigger data-testid="tab-settings" value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="analytics" className="mt-6 grid md:grid-cols-2 gap-4">
          <div className="p-6 bg-white rounded-2xl border border-gray-200">
            <div className="font-display text-xl mb-4">Revenue by Category</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={analytics?.categories || []}>
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip /><Bar dataKey="revenue" fill="#F48E2E" radius={[8,8,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="p-6 bg-white rounded-2xl border border-gray-200">
            <div className="font-display text-xl mb-4">Gender Distribution</div>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={analytics?.gender || []} dataKey="count" nameKey="label" outerRadius={90} label>
                  {(analytics?.gender || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Legend /><Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="p-6 bg-white rounded-2xl border border-gray-200">
            <div className="font-display text-xl mb-4">Top Cities</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={analytics?.cities || []}>
                <XAxis dataKey="label" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                <Tooltip /><Bar dataKey="count" fill="#FF9800" radius={[8,8,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="p-6 bg-white rounded-2xl border border-gray-200">
            <div className="font-display text-xl mb-4">Age Distribution</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={analytics?.ages || []}>
                <XAxis dataKey="label" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                <Tooltip /><Bar dataKey="count" fill="#2196F3" radius={[8,8,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>

        <TabsContent value="products" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={openP} onOpenChange={setOpenP}>
              <DialogTrigger asChild><Button data-testid="admin-add-product" className="rounded-full bg-[#F48E2E] hover:bg-[#D97815]"><PlusCircle className="h-4 w-4 mr-2" />Add Product</Button></DialogTrigger>
              <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>New product</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <label className="cursor-pointer flex items-center gap-2 px-3 py-2 border border-dashed rounded-xl">
                    <Upload className="h-4 w-4" /> <span className="text-sm">Choose image…</span>
                    <input type="file" accept="image/*" hidden onChange={readFile(setProd, "image_base64")} />
                  </label>
                  {prod.image_base64 && <img src={prod.image_base64} className="h-24 w-24 object-cover rounded-md" alt="" />}
                  <Input placeholder="Name" value={prod.name} onChange={(e) => setProd({ ...prod, name: e.target.value })} />
                  <Textarea placeholder="Description" value={prod.description} onChange={(e) => setProd({ ...prod, description: e.target.value })} />
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Price" type="number" value={prod.price} onChange={(e) => setProd({ ...prod, price: e.target.value })} />
                    <Input placeholder="MRP" type="number" value={prod.original_price} onChange={(e) => setProd({ ...prod, original_price: e.target.value })} />
                    <Select value={prod.category} onValueChange={(v) => setProd({ ...prod, category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{CATS.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <div>
                      <Label className="text-xs">Live for (hours, 48–240)</Label>
                      <Input data-testid="admin-prod-hours" type="number" min="48" max="240" value={prod.duration_hours} onChange={(e) => setProd({ ...prod, duration_hours: e.target.value })} />
                    </div>
                  </div>
                  <Button onClick={createProd} className="w-full rounded-full bg-[#F48E2E] hover:bg-[#D97815]">Publish</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {products.map((p) => (
              <div key={p.id} className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <div className="aspect-square bg-gray-100"><img src={p.image_base64} alt={p.name} className="w-full h-full object-cover" /></div>
                <div className="p-3">
                  <div className="text-sm font-medium truncate">{p.name}</div>
                  <div className="text-xs text-gray-500">{p.category} · ₹{p.price}</div>
                  <div className="text-[10px] mt-1 text-gray-400">{p.expired ? "Expired" : "Live"} · by {p.created_by_name || p.created_by_role}</div>
                  <div className="text-[10px] text-gray-400">Start {p.created_at ? new Date(p.created_at).toLocaleString("en-IN") : "—"}</div>
                  <div className="text-[10px] text-gray-400">Expiry {p.expires_at ? new Date(p.expires_at).toLocaleString("en-IN") : "—"}</div>
                  <div className="mt-2 flex items-center gap-1.5">
                    <input type="number" min="48" max="240" defaultValue={p.duration_hours} id={`hours-${p.id}`} data-testid={`admin-hours-${p.id}`} className="h-7 w-16 rounded border border-gray-200 px-1.5 text-[11px]" />
                    <button data-testid={`admin-hours-save-${p.id}`} onClick={() => saveProdHours(p.id)} className="text-[11px] text-[#F48E2E] font-medium hover:underline">Save hrs</button>
                  </div>
                  <div className="mt-1.5 flex items-center gap-3">
                    <button data-testid={`admin-toggle-prod-${p.id}`} onClick={() => toggleProdVisible(p)} className={`text-xs font-medium ${p.visible === false ? "text-green-600" : "text-amber-600"}`}>{p.visible === false ? "Activate" : "Deactivate"}</button>
                    <button data-testid={`admin-del-prod-${p.id}`} onClick={() => delProd(p.id)} className="text-xs text-red-500 flex items-center gap-1"><Trash2 className="h-3 w-3" /> Remove</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="banners" className="mt-6">
          <div className="mb-4 p-4 bg-white border border-gray-200 rounded-2xl" data-testid="admin-usp-image-card">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="font-display text-lg">Why CityShor — USP image</div>
                <div className="text-xs text-gray-500">Shown on the homepage inside the "A different way to shop online" panel. Recommended size 1200 × 900 px, JPG/PNG under 500 KB.</div>
              </div>
              <div className="flex gap-2">
                <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-full border border-dashed border-[#F48E2E] text-[#F48E2E] text-xs hover:bg-[#FEF3E6]">
                  <Upload className="h-3.5 w-3.5" /> Upload new image
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    data-testid="admin-usp-upload"
                    onChange={(e) => {
                      const f = e.target.files?.[0]; if (!f) return;
                      const r = new FileReader();
                      r.onload = async () => {
                        try {
                          await api.patch("/admin/site-content/usp_image", { value: r.result });
                          toast.success("Homepage USP image updated");
                          reload();
                          refreshSiteContent();
                        } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
                      };
                      r.readAsDataURL(f);
                      e.target.value = "";
                    }}
                  />
                </label>
                <Button
                  data-testid="admin-usp-reset"
                  variant="outline"
                  className="rounded-full text-xs h-9"
                  onClick={async () => {
                    if (!window.confirm("Reset to the default USP image?")) return;
                    try {
                      await api.patch("/admin/site-content/usp_image", { value: "" });
                      toast.success("Reset to default");
                      reload();
                      refreshSiteContent();
                    } catch (err) { toast.error(err.response?.data?.detail || "Reset failed"); }
                  }}
                >
                  Reset to default
                </Button>
              </div>
            </div>
            <div className="mt-3">
              <img
                data-testid="admin-usp-preview"
                src={siteContent.usp_image || "/images/usp-curated-exhibition.svg"}
                alt="USP preview"
                className="w-full max-w-md h-40 object-cover rounded-xl border border-gray-100"
              />
            </div>
          </div>

          <div className="flex justify-between items-center mb-4">
            <div className="font-display text-lg">Hero Banners</div>
            <Dialog open={openB} onOpenChange={setOpenB}>
              <DialogTrigger asChild><Button data-testid="admin-add-banner" className="rounded-full bg-[#F48E2E] hover:bg-[#D97815]"><PlusCircle className="h-4 w-4 mr-2" />Add Banner</Button></DialogTrigger>
              <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>New banner</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <label className="cursor-pointer flex items-center gap-2 px-3 py-2 border border-dashed rounded-xl">
                    <Upload className="h-4 w-4" /> <span className="text-sm">Choose image…</span>
                    <input type="file" accept="image/*" hidden onChange={readFile(setBanner, "image_base64")} />
                  </label>
                  {banner.image_base64 && <img src={banner.image_base64} className="h-24 w-full object-cover rounded-md" alt="" />}
                  <Input placeholder="Title" value={banner.title} onChange={(e) => setBanner({ ...banner, title: e.target.value })} />
                  <Input placeholder="Subtitle" value={banner.subtitle} onChange={(e) => setBanner({ ...banner, subtitle: e.target.value })} />
                  <Input placeholder="CTA text" value={banner.cta_text} onChange={(e) => setBanner({ ...banner, cta_text: e.target.value })} />
                  <Input placeholder="CTA link (e.g. /category/trending)" value={banner.cta_link} onChange={(e) => setBanner({ ...banner, cta_link: e.target.value })} />
                  <label className="flex items-start gap-2 p-3 rounded-xl border border-gray-200 bg-gray-50 cursor-pointer">
                    <input
                      data-testid="banner-show-overlay"
                      type="checkbox"
                      checked={!!banner.show_overlay}
                      onChange={(e) => setBanner({ ...banner, show_overlay: e.target.checked })}
                      className="mt-0.5 accent-[#F48E2E]"
                    />
                    <div>
                      <div className="text-sm font-medium">Show text overlay on this banner</div>
                      <div className="text-[11px] text-gray-500">Leave off for a clean image. Turn on to overlay the title, subtitle, brand text and "{banner.cta_text || 'Shop Now'}" button. Text only shows when this is on <b>and</b> a title is set. Banners with the same order number appear oldest first.</div>
                    </div>
                  </label>
                  <Button onClick={createBanner} className="w-full rounded-full bg-[#F48E2E] hover:bg-[#D97815]">Publish</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {banners.map((b) => (
              <div key={b.id} className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid={`banner-card-${b.id}`}>
                <img src={b.image_base64} alt={b.title} className="w-full h-40 object-cover" />
                <div className="p-3">
                  <div className="font-display text-lg">{b.title}</div>
                  <div className="text-sm text-gray-500">{b.subtitle}</div>
                  <div className="mt-3 flex items-center justify-between gap-3 p-2 rounded-xl bg-gray-50 border border-gray-200">
                    <div>
                      <div className="text-xs font-medium">Show text overlay</div>
                      <div className="text-[10px] text-gray-500">Title, subtitle, brand text and CTA button</div>
                    </div>
                    <button
                      data-testid={`banner-toggle-overlay-${b.id}`}
                      onClick={() => toggleBannerOverlay(b)}
                      role="switch"
                      aria-checked={!!b.show_overlay}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${b.show_overlay ? "bg-[#F48E2E]" : "bg-gray-300"}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${b.show_overlay ? "translate-x-6" : "translate-x-1"}`} />
                    </button>
                  </div>
                  <button data-testid={`banner-del-${b.id}`} onClick={() => delBanner(b.id)} className="mt-3 text-xs text-red-500 flex items-center gap-1"><Trash2 className="h-3 w-3" /> Remove</button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="vendors" className="mt-6 space-y-4">
          <div className="flex flex-wrap justify-between gap-3 items-center">
            <div className="text-sm text-gray-600">{vendors.length} exhibitor registration{vendors.length === 1 ? "" : "s"}</div>
            <Button data-testid="export-vendors" onClick={exportVendorsCsv} variant="outline" className="rounded-full">
              <Download className="h-4 w-4 mr-2" />Export Vendors (CSV)
            </Button>
          </div>
          {(() => {
            const pendingCount = vendors.filter((v) => v.kyc_status !== "verified" && v.kyc_status !== "rejected").length;
            return pendingCount > 0 ? (
              <div data-testid="kyc-waiting-list-banner" className="p-4 rounded-2xl bg-yellow-50 border border-yellow-200 flex items-center gap-2 text-yellow-800">
                <Bell className="h-4 w-4" />
                <span><b>{pendingCount}</b> vendor{pendingCount === 1 ? "" : "s"} awaiting KYC review — shown first below.</span>
              </div>
            ) : null;
          })()}
          {vendors.length === 0 ? (
            <div className="p-8 bg-white border border-gray-200 rounded-2xl text-center text-gray-500">No vendor registration requests yet.</div>
          ) : [...vendors].sort((a, b) => {
            const rank = (s) => (s !== "verified" && s !== "rejected") ? 0 : s === "rejected" ? 1 : 2;
            return rank(a.kyc_status) - rank(b.kyc_status);
          }).map((v) => {
            const k = v.kyc || {};
            const statusColor = v.kyc_status === "verified" ? "bg-green-100 text-green-700" : v.kyc_status === "rejected" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700";
            const ver = v.verification || { is_verified: false, computed: false, override: null, checks: {}, social: {}, product_count: 0, threshold: 10 };
            return (
              <div key={v.id} data-testid={`vendor-card-${v.id}`} className="p-5 bg-white border border-gray-200 rounded-2xl">
                <div className="flex flex-wrap justify-between gap-3 items-start">
                  <div className="flex-1 min-w-[240px]">
                    <div className="font-display text-lg inline-flex items-center gap-2">
                      {k.business_name || v.full_name}
                      {ver.is_verified && <VerifiedBadge size="h-4 w-4" testid={`vendor-verified-badge-${v.id}`} />}
                    </div>
                    <div className="text-sm text-gray-500">{v.email}{(v.phone || k.phone) ? `, ${v.phone || k.phone}` : ""}</div>
                    {v.vendor_code && (
                      <div className="mt-1 inline-block px-2 py-0.5 rounded-full bg-[#FEF3E6] text-[#F48E2E] font-mono-tab text-xs font-bold">{v.vendor_code}</div>
                    )}
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className={`text-[11px] px-2 py-1 rounded-full ${statusColor}`}>KYC: {v.kyc_status || "pending"}</span>
                      <span
                        data-testid={`vendor-account-status-${v.id}`}
                        className={`text-[11px] px-2 py-1 rounded-full font-medium ${
                          v.account_status === "suspended" ? "bg-red-100 text-red-700" :
                          v.account_status === "paused" ? "bg-amber-100 text-amber-700" :
                          "bg-green-100 text-green-700"
                        }`}
                      >
                        {(v.account_status || "active").replace(/^./, (c) => c.toUpperCase())}
                      </span>
                      <span
                        data-testid={`vendor-verified-status-${v.id}`}
                        className={`text-[11px] px-2 py-1 rounded-full inline-flex items-center gap-1 ${ver.is_verified ? "bg-blue-50 text-blue-700" : "bg-gray-100 text-gray-600"}`}
                      >
                        {ver.is_verified ? "Verified Vendor" : "Not verified"}
                        {ver.override !== null && ver.override !== undefined && (
                          <span className="ml-1 text-[10px] uppercase tracking-widest text-gray-500">manual</span>
                        )}
                      </span>
                    </div>
                    {v.account_status && v.account_status !== "active" && v.account_status_reason && (
                      <div data-testid={`vendor-status-reason-${v.id}`} className="mt-2 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                        Reason shown to vendor: <span className="text-gray-800">{v.account_status_reason}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {v.kyc_status !== "verified" && (
                      <Button data-testid={`verify-vendor-${v.id}`} size="sm" onClick={() => verifyVendor(v.id)} className="rounded-full bg-green-600 hover:bg-green-700 text-white">Approve KYC</Button>
                    )}
                    {v.kyc_status !== "rejected" && (
                      <Button data-testid={`reject-vendor-${v.id}`} size="sm" onClick={() => openKycRejectDialog(v)} variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">Reject KYC</Button>
                    )}
                    <Button data-testid={`vendor-set-active-${v.id}`} size="sm" onClick={() => setActive(v)} variant="outline" className={`rounded-full ${v.account_status === "active" ? "bg-green-600 hover:bg-green-700 text-white border-green-600" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}>Active</Button>
                    <Button data-testid={`vendor-set-paused-${v.id}`} size="sm" onClick={() => openStatusDialog(v, "paused")} variant="outline" className={`rounded-full ${v.account_status === "paused" ? "bg-amber-500 hover:bg-amber-600 text-white border-amber-500" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}>Pause</Button>
                    <Button data-testid={`vendor-set-suspended-${v.id}`} size="sm" onClick={() => openStatusDialog(v, "suspended")} variant="outline" className={`rounded-full ${v.account_status === "suspended" ? "bg-red-600 hover:bg-red-700 text-white border-red-600" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}>Suspend</Button>
                  </div>
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-[1fr_auto] border-t border-gray-100 pt-4" data-testid={`vendor-verified-panel-${v.id}`}>
                  <div className="text-xs text-gray-600 space-y-1">
                    <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Verified Vendor checks</div>
                    <ul className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {[
                        ["business_name", "Business name"],
                        ["phone", "Phone"],
                        ["business_address", "Address"],
                        ["products_min_10", `${ver.product_count ?? 0}/${ver.threshold ?? 10} products listed`],
                      ].map(([k2, label]) => (
                        <li key={k2} className="flex items-center gap-1.5">
                          <span className={`inline-block h-2 w-2 rounded-full ${ver.checks?.[k2] ? "bg-green-500" : "bg-gray-300"}`} />
                          <span>{label}</span>
                        </li>
                      ))}
                      <li className="flex items-center gap-1.5 col-span-2 text-gray-500">
                        <span className={`inline-block h-2 w-2 rounded-full ${ver.social?.instagram || ver.social?.facebook ? "bg-green-500" : "bg-gray-300"}`} />
                        <span>Instagram or Facebook link (optional)</span>
                      </li>
                      <li className="flex items-center gap-1.5 col-span-2 text-gray-500">
                        <span className={`inline-block h-2 w-2 rounded-full ${ver.social?.gst_number ? "bg-green-500" : "bg-gray-300"}`} />
                        <span>GST number (optional)</span>
                      </li>
                      <li className={`flex items-center gap-1.5 col-span-2 ${ver.agreements_accepted ? "text-gray-500" : "text-red-600"}`}>
                        <span className={`inline-block h-2 w-2 rounded-full ${ver.agreements_accepted ? "bg-green-500" : "bg-red-500"}`} />
                        <span>{ver.agreements_accepted ? "Vendor Agreement & Payout Policy accepted" : "Vendor Agreement & Payout Policy not accepted — needed before Mark Verified works"}</span>
                      </li>
                    </ul>
                  </div>
                  <div className="flex flex-col gap-2 items-stretch md:min-w-[220px]">
                    <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Manual override</div>
                    <div className="flex gap-2">
                      <Button
                        data-testid={`override-verify-${v.id}`}
                        size="sm"
                        onClick={() => setVerifiedOverride(v.id, true)}
                        className={`rounded-full flex-1 ${ver.override === true ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"}`}
                      >Mark Verified</Button>
                      <Button
                        data-testid={`override-unverify-${v.id}`}
                        size="sm"
                        onClick={() => setVerifiedOverride(v.id, false)}
                        className={`rounded-full flex-1 ${ver.override === false ? "bg-gray-800 hover:bg-black text-white" : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"}`}
                      >Force Unverified</Button>
                    </div>
                    {ver.override !== null && ver.override !== undefined && (
                      <button
                        data-testid={`override-clear-${v.id}`}
                        onClick={() => setVerifiedOverride(v.id, null)}
                        className="text-[11px] text-[#F48E2E] hover:text-[#D97815] underline text-left"
                      >Clear override, use auto rules</button>
                    )}
                  </div>
                </div>
                {k.business_name && (
                  <div className="mt-4 grid md:grid-cols-2 gap-y-2 gap-x-4 text-sm border-t border-gray-100 pt-4">
                    <div><span className="text-gray-500">Owner: </span>{k.owner_name || "-"}</div>
                    <div><span className="text-gray-500">PAN: </span><span className="font-mono-tab">{k.pan_number || "-"}</span></div>
                    <div><span className="text-gray-500">Aadhaar: </span><span className="font-mono-tab" data-testid="kyc-aadhaar-masked" title="Masked for privacy — full number available in the vendor's KYC document.">{maskAadhaar(k.aadhaar_number)}</span></div>
                    <div><span className="text-gray-500">GST: </span>{k.gst_number || "Not applicable"}</div>
                    <div className="md:col-span-2"><span className="text-gray-500">Address: </span>{k.business_address || "-"}</div>
                    <div><span className="text-gray-500">Bank holder: </span>{k.bank_holder_name || "-"}</div>
                    <div><span className="text-gray-500">Account: </span><span className="font-mono-tab">{k.bank_account_number ? "XXXX " + k.bank_account_number.slice(-4) : "-"}</span></div>
                    <div><span className="text-gray-500">IFSC: </span><span className="font-mono-tab">{k.bank_ifsc || "-"}</span></div>
                    <div><span className="text-gray-500">Submitted: </span>{k.submitted_at ? new Date(k.submitted_at).toLocaleString("en-IN") : "-"}</div>
                    {k.instagram_handle && <div><span className="text-gray-500">Instagram: </span>@{k.instagram_handle}</div>}
                    {k.facebook_handle && <div><span className="text-gray-500">Facebook: </span>{k.facebook_handle}</div>}
                    {k.website_url && <div className="md:col-span-2"><span className="text-gray-500">Website: </span><a href={k.website_url} target="_blank" rel="noreferrer" className="text-[#F48E2E] hover:underline">{k.website_url}</a></div>}
                    <div className="md:col-span-2 mt-2 p-3 rounded-xl bg-[#FEF3E6] border border-[#F48E2E]/30" data-testid={`kyc-acceptance-${v.id}`}>
                      <div className="text-[11px] uppercase tracking-widest text-[#F48E2E] font-bold mb-1">Agreement Acceptance</div>
                      <div className="grid md:grid-cols-2 gap-y-1 gap-x-4 text-xs text-gray-700">
                        <div>
                          <span className="text-gray-500">Vendor Agreement: </span>
                          {k.agreement_accepted
                            ? <span className="text-green-700 font-medium">Accepted{k.agreement_version ? ` · ${k.agreement_version}` : ""}</span>
                            : <span className="text-red-600 font-medium">Not accepted</span>}
                        </div>
                        <div>
                          <span className="text-gray-500">Payout Policy: </span>
                          {k.payout_policy_accepted
                            ? <span className="text-green-700 font-medium">Accepted{k.payout_policy_version ? ` · ${k.payout_policy_version}` : ""}</span>
                            : <span className="text-red-600 font-medium">Not accepted</span>}
                        </div>
                        <div><span className="text-gray-500">Accepted at: </span>{k.accepted_at ? new Date(k.accepted_at).toLocaleString("en-IN") : "-"}</div>
                        <div><span className="text-gray-500">IP address: </span><span className="font-mono-tab">{k.acceptance_ip || "-"}</span></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="payouts" className="mt-6">
          <div className="flex flex-wrap justify-end gap-2 mb-4">
            <Button data-testid="export-gst-report" onClick={exportGstReport} variant="outline" className="rounded-full">
              <Download className="h-4 w-4 mr-2" />Export GST Report (GSTR-1)
            </Button>
            <Button data-testid="export-vendor-gst" onClick={exportVendorGstReport} variant="outline" className="rounded-full">
              <Download className="h-4 w-4 mr-2" />Vendor GST Summary
            </Button>
          </div>
          <AdminPayouts />
        </TabsContent>

        <TabsContent value="returns" className="mt-6">
          <AdminReturnsTab />
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Button data-testid="orders-filter-all" variant={orderFilter === "all" ? "default" : "outline"} className="rounded-full h-8 text-xs" onClick={() => setOrderFilter("all")}>All orders</Button>
            <Button data-testid="orders-filter-bank" variant={orderFilter === "bank" ? "default" : "outline"} className="rounded-full h-8 text-xs" onClick={() => setOrderFilter("bank")}>
              Awaiting bank transfer ({orders.filter(isAwaitingBankTransfer).length})
            </Button>
            <label className="flex items-center gap-1.5 text-xs text-gray-600 ml-2 cursor-pointer">
              <input data-testid="orders-show-archived" type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-[#F48E2E]" />
              Show archived ({orders.filter((o) => o.archived).length})
            </label>
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="bg-gray-50 text-left"><tr>
                <th className="p-3">Order</th><th className="p-3">Email</th><th className="p-3">Vendors</th><th className="p-3">Total</th><th className="p-3">Payment</th><th className="p-3">Status</th><th className="p-3">Date</th><th className="p-3">Shiprocket</th><th className="p-3">Actions</th>
              </tr></thead>
              <tbody>{orders.filter((o) => (showArchived || !o.archived) && (orderFilter === "all" || isAwaitingBankTransfer(o))).map((o) => (
                <tr key={o.id} className="border-t">
                  <td className="p-3 font-mono-tab text-xs">{o.id.slice(0, 8)}{o.archived && <span className="ml-1 text-[9px] uppercase bg-gray-200 text-gray-600 rounded px-1">archived</span>}</td>
                  <td className="p-3">{o.user_email}</td>
                  <td className="p-3 text-xs">{(o.vendor_names || []).join(", ") || (o.vendor_codes || []).join(", ") || "-"}</td>
                  <td className="p-3">₹{o.total.toLocaleString("en-IN")}</td>
                  <td className="p-3">{o.payment_method}</td>
                  <td className="p-3 capitalize">{(o.status || "").replace(/_/g, " ")}<div className="text-[10px] normal-case text-gray-500">{(o.payment_status || "").replace(/_/g, " ")}</div></td>
                  <td className="p-3 text-xs text-gray-500">{new Date(o.created_at).toLocaleString("en-IN")}</td>
                  <td className="p-3 text-xs" data-testid={`order-shiprocket-${o.id}`}>
                    {(() => {
                      const sr = (o.shipments || []).find((s) => s.shipment_id);
                      if (sr) {
                        return (
                          <div className="text-green-700">
                            <div>Sent · Order #{sr.shiprocket_order_id || "-"}</div>
                            <div className="font-mono-tab">AWB {sr.awb_code || "pending"}{sr.courier_name ? ` · ${sr.courier_name}` : ""}</div>
                            {!sr.awb_code && <div className="text-red-600">AWB not assigned{sr.awb_error ? `: ${sr.awb_error}` : ""}</div>}
                            <div className="text-gray-500">{sr.pickup_requested_at ? `Pickup requested ${new Date(sr.pickup_requested_at).toLocaleString("en-IN")}` : "Pickup not scheduled"}</div>
                            {sr.pickup_error && <div className="text-red-600">{sr.pickup_error}</div>}
                          </div>
                        );
                      }
                      if (o.shiprocket_error) return <div className="text-red-600">Failed: {o.shiprocket_error}</div>;
                      if (o.status === "cancelled") return <span className="text-gray-400">-</span>;
                      if (["processing", "packed", "shipped", "delivered"].includes(o.status)) return <span className="text-amber-700">Confirmed, not sent to Shiprocket</span>;
                      return <span className="text-gray-400">Not sent (waiting for vendor confirmation)</span>;
                    })()}
                  </td>
                  <td className="p-3">
                    {isAwaitingBankTransfer(o) && (
                      <div className="flex flex-col gap-1 mb-1">
                        <Button data-testid={`order-mark-paid-${o.id}`} disabled={payActionBusy === o.id} onClick={() => bankTransferAction(o, "mark-payment-received")} className="h-7 rounded-full bg-green-600 hover:bg-green-700 text-white text-[11px] px-3">Mark payment received</Button>
                        <Button data-testid={`order-reject-payment-${o.id}`} disabled={payActionBusy === o.id} onClick={() => bankTransferAction(o, "reject-payment")} variant="outline" className="h-7 rounded-full text-red-600 border-red-200 hover:bg-red-50 text-[11px] px-3">Reject</Button>
                      </div>
                    )}
                    {o.status === "cancelled" && o.refund_status && o.refund_status !== "not_required" && (
                      <div className="mb-1"><AdminRefundControl kind="order" target={o} compact onDone={reload} /></div>
                    )}
                    <AdminOrderActions order={o} onDone={reload} />
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="customers" className="mt-6">
          <AdminCustomerAnalytics />
        </TabsContent>

        <TabsContent value="notifications" className="mt-6" data-testid="notifications-tab">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="p-6 bg-white rounded-2xl border border-gray-200">
              <div className="font-display text-xl mb-1">Send Announcement to Buyers</div>
              <div className="text-xs text-gray-500 mb-3">Broadcasts to every buyer's in-app bell, and (once FCM keys are configured) their web browser as a push notification.</div>
              <Input data-testid="announce-title" placeholder="Title (e.g. Flash weekend sale)" value={announceForm.title} onChange={(e) => setAnnounceForm({ ...announceForm, title: e.target.value })} className="mb-2" />
              <Textarea data-testid="announce-body" placeholder="Body — keep it short and specific" value={announceForm.body} onChange={(e) => setAnnounceForm({ ...announceForm, body: e.target.value })} rows={3} className="mb-2" />
              <Input data-testid="announce-link" placeholder="Deep link (e.g. /category/trending)" value={announceForm.link} onChange={(e) => setAnnounceForm({ ...announceForm, link: e.target.value })} className="mb-3" />
              <Button data-testid="announce-send" onClick={sendAnnouncement} disabled={announceBusy} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815]">
                <Send className="h-4 w-4 mr-2" />{announceBusy ? "Sending…" : "Send to all buyers"}
              </Button>
            </div>

            <div className="p-6 bg-white rounded-2xl border border-gray-200">
              <div className="font-display text-xl mb-1">Send Vendor / All-users Notification</div>
              <div className="text-xs text-gray-500 mb-3">In-app bell only. Use the Announcement panel on the left for buyer web-push broadcasts.</div>
              <Input placeholder="Title" value={notif.title} onChange={(e) => setNotif({ ...notif, title: e.target.value })} className="mb-2" />
              <Textarea placeholder="Body" value={notif.body} onChange={(e) => setNotif({ ...notif, body: e.target.value })} className="mb-2" />
              <Select value={notif.target} onValueChange={(v) => setNotif({ ...notif, target: v })}>
                <SelectTrigger className="mb-3"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All users</SelectItem>
                  <SelectItem value="buyers">Buyers only</SelectItem>
                  <SelectItem value="vendors">Vendors only</SelectItem>
                </SelectContent>
              </Select>
              <Button data-testid="send-notif" onClick={sendNotif} className="rounded-full bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"><Send className="h-4 w-4 mr-2" />Send</Button>
            </div>
          </div>

          <div className="mt-8" data-testid="announcement-history">
            <div className="font-display text-xl mb-3">Announcement History</div>
            {announcements.length === 0 ? (
              <div className="p-8 bg-white border border-gray-200 rounded-2xl text-center text-gray-500 text-sm">No announcements sent yet.</div>
            ) : (
              <div className="grid gap-3">
                {announcements.map((a) => (
                  <div key={a.id} data-testid={`announcement-row-${a.id}`} className="p-4 bg-white border border-gray-200 rounded-2xl flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{a.title}</div>
                      <div className="text-sm text-gray-600 mt-0.5">{a.body}</div>
                      <div className="text-[11px] text-gray-500 mt-2 flex flex-wrap gap-2">
                        <span>Sent {new Date(a.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                        <span>· {a.buyer_count} buyer{a.buyer_count === 1 ? "" : "s"}</span>
                        {a.link && a.link !== "/" && <span>· link {a.link}</span>}
                        {a.fcm_result?.skipped ? (
                          <span className="text-amber-600">· push queued — FCM not configured yet</span>
                        ) : a.fcm_result?.sent > 0 && (
                          <span className="text-green-700">· {a.fcm_result.sent} push delivered</span>
                        )}
                      </div>
                    </div>
                    <Button data-testid={`announcement-resend-${a.id}`} onClick={() => resendAnnouncement(a.id)} variant="outline" size="sm" className="rounded-full">
                      <Send className="h-3 w-3 mr-1" /> Re-send
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="legal" className="mt-6" data-testid="legal-tab">
          <div className="mb-4">
            <div className="font-display text-xl">Legal Policies</div>
            <p className="text-xs text-gray-500 mt-1">Edit any policy body. Basic HTML is supported (h2, p, ul, li, strong, a). The final lawyer-approved wording can be pasted here anytime — it replaces the placeholder instantly.</p>
          </div>
          <div className="grid gap-3">
            {legalDocs.map((d) => (
              <div key={d.slug} data-testid={`legal-row-${d.slug}`} className="p-4 bg-white border border-gray-200 rounded-2xl">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium">{d.title}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      /legal/{d.slug} · {d.audience}
                      {d.updated_at && <> · updated {new Date(d.updated_at).toLocaleDateString("en-IN")}</>}
                    </div>
                  </div>
                  {legalEditing !== d.slug && (
                    <Button data-testid={`legal-edit-${d.slug}`} onClick={() => startLegalEdit(d)} variant="outline" className="rounded-full h-9">
                      Edit
                    </Button>
                  )}
                </div>
                {legalEditing === d.slug && (
                  <div className="mt-4 space-y-3">
                    <Textarea
                      data-testid={`legal-textarea-${d.slug}`}
                      value={legalDraft}
                      onChange={(e) => setLegalDraft(e.target.value)}
                      rows={14}
                      className="font-mono text-xs"
                    />
                    <div className="flex gap-2">
                      <Button data-testid={`legal-save-${d.slug}`} onClick={saveLegal} disabled={legalBusy} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815]">
                        {legalBusy ? "Saving…" : "Save changes"}
                      </Button>
                      <Button variant="outline" onClick={() => { setLegalEditing(null); setLegalDraft(""); }} className="rounded-full">Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="content" className="mt-6" data-testid="content-tab">
          <AdminCuratedDropCard />
          <AdminMenuLinksCard />
          <div className="mb-4">
            <div className="font-display text-xl">Content Management</div>
            <p className="text-xs text-gray-500 mt-1">Edit any label on the site. Changes save per row and reflect immediately for all visitors. Product/banner content live in their own tabs, legal text in the Legal tab.</p>
          </div>
          {(() => {
            const GROUPS = [
              { title: "Homepage", prefix: "home." },
              { title: "About Us", prefix: "about." },
              { title: "Footer", prefix: "footer." },
              { title: "Contact & Social", prefixes: ["contact.", "social."] },
            ];
            const matches = (k, g) => (g.prefixes || [g.prefix]).some((p) => k.startsWith(p));
            return GROUPS.map((g) => {
              const curatedKeys = ["home.hero_kicker", "home.hero_headline", "home.hero_subtext"];
              const keys = Object.keys(siteContent).filter((k) => matches(k, g) && !curatedKeys.includes(k)).sort();
              if (!keys.length) return null;
              return (
                <div key={g.title} className="mb-8">
                  <div className="font-display text-lg mb-3">{g.title}</div>
                  <div className="space-y-3">
                    {keys.map((k) => (
                      <ContentRow
                        key={k}
                        contentKey={k}
                        initial={siteContent[k]}
                        busy={!!contentBusy[k]}
                        onSave={saveContent}
                      />
                    ))}
                  </div>
                </div>
              );
            });
          })()}
        </TabsContent>

        <TabsContent value="fees" className="mt-6" data-testid="fees-tab-content">
          <CommissionFeesTab />
        </TabsContent>

        <TabsContent value="boosts" className="mt-6" data-testid="boosts-tab-content">
          <AdminBoostsTab />
        </TabsContent>

        <TabsContent value="drops" className="mt-6" data-testid="drops-tab-content">
          <AdminDropsTab />
        </TabsContent>

        <TabsContent value="grievance" className="mt-6" data-testid="grievance-tab-content">
          <AdminGrievanceTab />
        </TabsContent>

        <TabsContent value="team" className="mt-6" data-testid="team-tab-content">
          <AdminTeamTab />
        </TabsContent>

        <TabsContent value="exports" className="mt-6" data-testid="exports-tab-content">
          <AdminExportsTab />
        </TabsContent>

        <TabsContent value="settings" className="mt-6" data-testid="settings-tab-content">
          <AdminBankTab />
          <AdminCodSettings />
          <AdminRefundSettings />
        </TabsContent>

        <TabsContent value="live" className="mt-6" data-testid="live-tab-content">
          <AdminLiveTab />
        </TabsContent>

        <TabsContent value="categories" className="mt-6" data-testid="categories-tab">          <div className="mb-4">
            <div className="font-display text-xl">Categories &amp; Subcategories</div>
            <p className="text-xs text-gray-500 mt-1">Add or edit categories used on the homepage grid and vendor product form. Changes go live immediately.</p>
          </div>

          <div className="p-4 bg-white border border-gray-200 rounded-2xl mb-6">
            <div className="font-medium mb-3">{catEditing ? `Edit "${catEditing}"` : "Add a new category"}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Name <span className="text-red-500">*</span></Label>
                <Input data-testid="cat-name" value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} placeholder="e.g. Home Decor" />
              </div>
              {!catEditing && (
                <div>
                  <Label>Slug <span className="text-gray-400 font-normal">optional, auto</span></Label>
                  <Input data-testid="cat-slug" value={catForm.slug} onChange={(e) => setCatForm({ ...catForm, slug: e.target.value })} placeholder="home-decor" />
                </div>
              )}
              <div>
                <Label>Icon (lucide name)</Label>
                <Input data-testid="cat-icon" value={catForm.icon} onChange={(e) => setCatForm({ ...catForm, icon: e.target.value })} placeholder="e.g. gem, gift, watch, shirt" />
              </div>
              <div>
                <Label>Display Order</Label>
                <Input data-testid="cat-order" type="number" value={catForm.display_order} onChange={(e) => setCatForm({ ...catForm, display_order: e.target.value })} />
              </div>
              <div>
                <Label>Parent category <span className="text-gray-400 font-normal">optional (for subcategories)</span></Label>
                <Select value={catForm.parent_slug || "__none__"} onValueChange={(v) => setCatForm({ ...catForm, parent_slug: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="cat-parent"><SelectValue placeholder="Top level" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Top level (no parent)</SelectItem>
                    {cats.filter((c) => c.slug !== catEditing && !c.parent_slug).map((c) => (
                      <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Default GST %</Label>
                <Select value={String(catForm.gst_default)} onValueChange={(v) => setCatForm({ ...catForm, gst_default: Number(v) })}>
                  <SelectTrigger data-testid="cat-gst"><SelectValue /></SelectTrigger>
                  <SelectContent>{[0, 5, 12, 18, 28].map((g) => <SelectItem key={g} value={String(g)}>{g}%</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button data-testid="cat-save" onClick={saveCategory} disabled={catBusy} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815]">{catBusy ? "Saving…" : catEditing ? "Save changes" : "Add category"}</Button>
              {catEditing && <Button variant="outline" onClick={resetCatForm} className="rounded-full">Cancel</Button>}
            </div>
          </div>

          <div className="grid gap-2">
            {(() => {
              const parents = cats.filter((c) => !c.parent_slug).sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
              const orphans = cats.filter((c) => c.parent_slug && !parents.some((p) => p.slug === c.parent_slug));
              const renderRow = (c, isChild = false) => (
                <div key={c.slug} data-testid={`cat-row-${c.slug}`} className={`p-3 bg-white border border-gray-200 rounded-xl flex items-center justify-between gap-3 ${isChild ? "ml-6 border-l-2 border-l-[#F48E2E]/40" : ""}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{isChild ? "↳ " : ""}{c.name}</div>
                    <div className="text-[11px] text-gray-500 font-mono">{c.slug} · order {c.display_order ?? 0} · GST {c.gst_default ?? 18}% {c.icon ? `· ${c.icon}` : ""}</div>
                  </div>
                  <div className="flex gap-2 text-xs">
                    <button data-testid={`cat-edit-${c.slug}`} onClick={() => startCatEdit(c)} className="p-1.5 rounded-full text-[#F48E2E] hover:bg-orange-50"><Pencil className="h-3.5 w-3.5" /></button>
                    <button data-testid={`cat-del-${c.slug}`} onClick={() => deleteCategory(c.slug)} className="p-1.5 rounded-full text-red-500 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              );
              return (
                <>
                  {parents.map((p) => {
                    const children = cats
                      .filter((c) => c.parent_slug === p.slug)
                      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
                    return (
                      <div key={p.slug} className="space-y-1.5">
                        {renderRow(p)}
                        {children.map((c) => renderRow(c, true))}
                      </div>
                    );
                  })}
                  {orphans.length > 0 && (
                    <div className="pt-2">
                      <div className="text-[11px] uppercase tracking-widest text-gray-500 font-mono mb-1">Unlinked subcategories</div>
                      {orphans.map((c) => renderRow(c, true))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={statusDialog.open} onOpenChange={(o) => !o && setStatusDialog({ open: false, vendor: null, target: "paused", reason: "" })}>
        <DialogContent data-testid="vendor-status-dialog">
          <DialogHeader>
            <DialogTitle>
              {statusDialog.target === "paused" ? "Pause vendor account" : "Suspend vendor account"}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-gray-600 mb-3">
            {statusDialog.target === "paused"
              ? "The vendor will not be able to upload new products, but their existing listings will stay visible to buyers."
              : "All the vendor's listings will be hidden from buyers immediately and sign-in will be blocked. Use this only for serious violations."}
          </div>
          <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-1">Reason (shown to the vendor)</div>
          <Textarea
            data-testid="vendor-status-reason"
            placeholder={statusDialog.target === "paused" ? "e.g. Repeated late dispatch — please review your fulfilment SLA before we lift the pause." : "e.g. Multiple buyer complaints of counterfeit products under review."}
            rows={4}
            value={statusDialog.reason}
            onChange={(e) => setStatusDialog({ ...statusDialog, reason: e.target.value })}
          />
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setStatusDialog({ open: false, vendor: null, target: "paused", reason: "" })}>Cancel</Button>
            <Button
              data-testid="vendor-status-submit"
              onClick={submitStatusChange}
              className={`rounded-full ${statusDialog.target === "paused" ? "bg-amber-500 hover:bg-amber-600" : "bg-red-600 hover:bg-red-700"} text-white`}
            >
              {statusDialog.target === "paused" ? "Pause vendor" : "Suspend vendor"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={kycRejectDialog.open} onOpenChange={(o) => !o && setKycRejectDialog({ open: false, vendor: null, reason: "" })}>
        <DialogContent data-testid="kyc-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject KYC</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-gray-600 mb-3">
            The vendor will be notified their exhibitor application could not be verified, along with the reason below.
          </div>
          <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-1">Reason (shown to the vendor)</div>
          <Textarea
            data-testid="kyc-reject-reason"
            placeholder="e.g. PAN and Aadhaar names do not match — please resubmit with matching documents."
            rows={4}
            value={kycRejectDialog.reason}
            onChange={(e) => setKycRejectDialog({ ...kycRejectDialog, reason: e.target.value })}
          />
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setKycRejectDialog({ open: false, vendor: null, reason: "" })}>Cancel</Button>
            <Button data-testid="kyc-reject-submit" onClick={submitKycReject} className="rounded-full bg-red-600 hover:bg-red-700 text-white">
              Reject KYC
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ContentRow({ contentKey, initial, busy, onSave }) {
  const [val, setVal] = useState(initial || "");
  const dirty = val !== (initial || "");
  const isLong = (initial || "").length > 80;
  return (
    <div data-testid={`content-row-${contentKey}`} className="p-3 bg-white border border-gray-200 rounded-xl">
      <div className="text-[11px] uppercase tracking-widest text-gray-500 font-mono mb-1">{contentKey}</div>
      <div className="flex gap-2 items-start">
        {isLong ? (
          <Textarea data-testid={`content-input-${contentKey}`} value={val} onChange={(e) => setVal(e.target.value)} rows={3} className="flex-1 text-sm" />
        ) : (
          <Input data-testid={`content-input-${contentKey}`} value={val} onChange={(e) => setVal(e.target.value)} className="flex-1 text-sm" />
        )}
        <Button
          data-testid={`content-save-${contentKey}`}
          onClick={() => onSave(contentKey, val)}
          disabled={busy || !dirty}
          className="rounded-full h-9 bg-[#F48E2E] hover:bg-[#D97815] disabled:opacity-40"
        >{busy ? "…" : "Save"}</Button>
      </div>
    </div>
  );
}
